/** Cytoscape.js graph visualization wrapper with focus mode and search-highlight */

import { useRef, useEffect, useCallback, useImperativeHandle, forwardRef } from 'react'
import cytoscape from 'cytoscape'
import type { Core, EventObject } from 'cytoscape'
import { EDGE_STYLES, CATEGORY_COLORS, DEFAULT_NODE_COLOR, CY_STYLE } from './graph-canvas-styles'
import { toElements, applyFocusMode, clearFocusMode, applyHoverHighlight, clearHoverHighlight, applyLoD, showLabelOnHover, hideLabelAfterHover } from './graph-canvas-helpers'
import type { GraphResponse } from '@agentwiki/shared'

/** Public imperative handle for parent components */
export interface GraphCanvasHandle {
  fitToView: () => void
  highlightSearch: (query: string) => void
  clearHighlight: () => void
  centerOnNode: (nodeId: string) => void
}

interface GraphCanvasProps {
  data: GraphResponse | undefined
  onNodeSelect?: (nodeId: string) => void
  onNodeNavigate?: (nodeId: string) => void
  /** GH-258: called on node mouseover with nodeId + canvas-relative x/y (px); null = mouseout */
  onNodeHover?: (nodeId: string | null, x: number, y: number) => void
  className?: string
}

export const GraphCanvas = forwardRef<GraphCanvasHandle, GraphCanvasProps>(
  function GraphCanvas({ data, onNodeSelect, onNodeNavigate, onNodeHover, className = '' }, ref) {
    const containerRef = useRef<HTMLDivElement>(null)
    const cyRef = useRef<Core | null>(null)
    const callbacksRef = useRef({ onNodeSelect, onNodeNavigate, onNodeHover })
    callbacksRef.current = { onNodeSelect, onNodeNavigate, onNodeHover }

    /** Expose imperative handle to parent */
    useImperativeHandle(ref, () => ({
      fitToView: () => {
        const cy = cyRef.current
        if (!cy) return
        clearFocusMode(cy)
        clearHoverHighlight(cy)
        cy.fit(undefined, 40)
      },
      highlightSearch: (query: string) => {
        const cy = cyRef.current
        if (!cy) return
        if (!query.trim()) { cy.nodes().removeClass('search-match'); return }
        clearFocusMode(cy)
        clearHoverHighlight(cy)
        cy.nodes().removeClass('search-match')
        const lowerQuery = query.toLowerCase()
        cy.nodes().forEach((node) => {
          const label = (node.data('label') ?? '').toLowerCase()
          if (label.includes(lowerQuery)) node.addClass('search-match')
        })
        // Center viewport on matches
        const matches = cy.nodes('.search-match')
        if (matches.length) cy.animate({ fit: { eles: matches, padding: 60 }, duration: 400 })
      },
      clearHighlight: () => {
        cyRef.current?.nodes().removeClass('search-match')
      },
      centerOnNode: (nodeId: string) => {
        const cy = cyRef.current
        if (!cy) return
        const node = cy.getElementById(nodeId)
        if (node.length) cy.animate({ center: { eles: node }, zoom: Math.max(cy.zoom(), 1.5), duration: 400 })
      },
    }), [])

    /** Initialize Cytoscape once */
    useEffect(() => {
      if (!containerRef.current) return

      const cy = cytoscape({
        container: containerRef.current,
        elements: [],
        style: CY_STYLE as never,
        layout: { name: 'preset' },
        minZoom: 0.2,
        maxZoom: 3,
        wheelSensitivity: 0.3,
      })

      // Node tap: select + apply focus mode
      cy.on('tap', 'node', (evt: EventObject) => {
        const nodeId = evt.target.id()
        callbacksRef.current.onNodeSelect?.(nodeId)
        applyFocusMode(cy, nodeId)
      })

      // Double-tap: navigate to document
      cy.on('dbltap', 'node', (evt: EventObject) => {
        callbacksRef.current.onNodeNavigate?.(evt.target.id())
      })

      // Tap on background: clear focus + hover
      cy.on('tap', (evt: EventObject) => {
        if (evt.target === cy) {
          clearFocusMode(cy)
          clearHoverHighlight(cy)
        }
      })

      // Hover: highlight neighborhood + smart labels + tooltip (GH-128, GH-258)
      cy.on('mouseover', 'node', (evt: EventObject) => {
        const node = evt.target
        applyHoverHighlight(cy, node.id())
        showLabelOnHover(cy, node.id())

        // GH-258: fire provenance tooltip callback with canvas-relative position
        const renderedPos = node.renderedPosition()
        callbacksRef.current.onNodeHover?.(node.id(), renderedPos.x + 12, renderedPos.y + 12)
      })
      cy.on('mouseout', 'node', (evt: EventObject) => {
        clearHoverHighlight(cy)
        hideLabelAfterHover(cy, evt.target.id())
        // GH-258: hide provenance tooltip
        callbacksRef.current.onNodeHover?.(null, 0, 0)
      })

      // Level of Detail: adjust visibility based on zoom level (throttled)
      let lodTimer: ReturnType<typeof setTimeout> | null = null
      cy.on('zoom', () => {
        if (lodTimer) return
        lodTimer = setTimeout(() => { lodTimer = null; applyLoD(cy) }, 16)
      })

      cyRef.current = cy
      return () => {
        cy.destroy()
        cyRef.current = null
      }
    }, []) // mount once

    /** Update elements when data changes (preserve zoom/pan state) */
    useEffect(() => {
      const cy = cyRef.current
      if (!cy || !data) return

      cy.elements().remove()
      cy.add(toElements(data))

      // Scale layout parameters with graph size for better spacing
      const nodeCount = data.nodes.length
      const repulsion = nodeCount > 80 ? 20000 : nodeCount > 40 ? 16000 : 12000
      const edgeLen = nodeCount > 80 ? 220 : nodeCount > 40 ? 180 : 160

      cy.layout({
        name: 'cose',
        animate: false,
        nodeRepulsion: () => repulsion,
        idealEdgeLength: () => edgeLen,
        gravity: nodeCount > 40 ? 0.15 : 0.25,
        numIter: 500,
        nodeDimensionsIncludeLabels: true,
        padding: 30,
      } as never).run()
    }, [data])

    const handleFit = useCallback(() => {
      const cy = cyRef.current
      if (!cy) return
      clearFocusMode(cy)
      clearHoverHighlight(cy)
      cy.fit(undefined, 40)
    }, [])

    const handleZoomIn = useCallback(() => {
      const cy = cyRef.current
      if (cy) cy.zoom({ level: cy.zoom() * 1.3, renderedPosition: { x: cy.width() / 2, y: cy.height() / 2 } })
    }, [])

    const handleZoomOut = useCallback(() => {
      const cy = cyRef.current
      if (cy) cy.zoom({ level: cy.zoom() / 1.3, renderedPosition: { x: cy.width() / 2, y: cy.height() / 2 } })
    }, [])

    return (
      <div className={`relative flex-1 ${className}`}>
        <div ref={containerRef} className="h-full w-full" />

        {/* Zoom controls */}
        <div className="absolute bottom-4 right-4 flex flex-col gap-1">
          <button
            onClick={handleZoomIn}
            className="flex h-8 w-8 items-center justify-center rounded-md bg-white dark:bg-surface-2 text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:bg-surface-3 hover:text-neutral-900 dark:text-neutral-100"
            title="Zoom in"
          >
            +
          </button>
          <button
            onClick={handleZoomOut}
            className="flex h-8 w-8 items-center justify-center rounded-md bg-white dark:bg-surface-2 text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:bg-surface-3 hover:text-neutral-900 dark:text-neutral-100"
            title="Zoom out"
          >
            &minus;
          </button>
          <button
            onClick={handleFit}
            className="flex h-8 w-8 items-center justify-center rounded-md bg-white dark:bg-surface-2 text-xs text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:bg-surface-3 hover:text-neutral-900 dark:text-neutral-100"
            title="Reset view"
          >
            &boxbox;
          </button>
        </div>

        {/* Legend — hidden on small phones (GH-128: added node categories) */}
        <div className="absolute bottom-4 left-4 hidden rounded-lg bg-white/90 dark:bg-surface-1/90 p-3 backdrop-blur-sm sm:block">
          <div className="mb-1.5 text-[10px] font-medium uppercase tracking-wider text-neutral-500">Node Types</div>
          <div className="mb-2 flex flex-col gap-1">
            {Object.entries(CATEGORY_COLORS).map(([cat, color]) => (
              <div key={cat} className="flex items-center gap-2">
                <div className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: color }} />
                <span className="text-[10px] text-neutral-500 dark:text-neutral-400">{cat}</span>
              </div>
            ))}
            <div className="flex items-center gap-2">
              <div className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: DEFAULT_NODE_COLOR }} />
              <span className="text-[10px] text-neutral-500 dark:text-neutral-400">other</span>
            </div>
          </div>
          <div className="mb-1.5 text-[10px] font-medium uppercase tracking-wider text-neutral-500">Edge Types</div>
          <div className="flex flex-col gap-1">
            {Object.entries(EDGE_STYLES).map(([type, style]) => (
              <div key={type} className="flex items-center gap-2">
                <div className="h-0.5 w-4" style={{ backgroundColor: style.color, borderStyle: style.lineStyle === 'dashed' ? 'dashed' : 'solid' }} />
                <span className="text-[10px] text-neutral-500 dark:text-neutral-400">{type}</span>
              </div>
            ))}
            <div className="flex items-center gap-2">
              <div className="h-0.5 w-4 border-t border-dotted border-neutral-600" />
              <span className="text-[10px] text-neutral-500 dark:text-neutral-400">similarity</span>
            </div>
          </div>
        </div>
      </div>
    )
  },
)
